import { useRef, useState } from 'react';
import { Alert, Platform } from 'react-native';
import type { Anexo } from '../store/types';
import type { OrigemAnexo } from '../lib/anexar';
import { useAnexar } from './MenuAnexo';
import { useCores } from './theme';
import { ActionSheet } from './ui';
import { t } from '../lib/i18n';

type Res = { arq: Anexo; aviso?: string } | null;

/** De onde vem a imagem que a IA vai ler: Fotos, Tirar foto ou Arquivos (0.72, texto 11g: os mesmos nomes e o
 *  mesmo caminho do menu de anexos; Fotos sem a galeria neste app mostra o aviso).
 *  No navegador vai direto para o seletor de arquivos. */
export function useFonteIA() {
  const { accent } = useCores();
  const anexo = useAnexar({ cor: accent });
  const pegar = (origem: OrigemAnexo, protegido: boolean): Promise<Res> =>
    anexo.anexar({ origem, tipo: 'tudo', semNuvem: protegido }).then(r => (r ? { arq: r.anexo, aviso: r.aviso } : null));
  const [aberta, setAberta] = useState(false);
  const pedido = useRef<{ protegido: boolean; fim: (r: Res) => void; escolheu: boolean; ja?: Anexo } | null>(null);

  /** ja: arquivo que já está na ficha (aparece como primeira opção). */
  const escolher = (protegido = false, ja?: Anexo): Promise<Res> => {
    if (Platform.OS === 'web' && !ja) return pegar('arquivos', protegido);
    return new Promise(res => { pedido.current = { protegido, fim: res, escolheu: false, ja }; setAberta(true); });
  };
  // a folha chama "fechar" e, se a pessoa tocou numa opção, a opção 250 ms depois
  const fechar = () => {
    setAberta(false);
    const p = pedido.current;
    setTimeout(() => { if (p && !p.escolheu && pedido.current === p) { pedido.current = null; p.fim(null); } }, 600);
  };
  const rodar = (acao: (protegido: boolean) => Promise<Res>) => {
    const p = pedido.current;
    if (!p) return;
    p.escolheu = true;
    pedido.current = null;
    // espera a folha sumir antes de abrir a câmera ou o seletor (no iPhone duas janelas juntas travam)
    setTimeout(() => { acao(p.protegido).then(p.fim, e => { Alert.alert(t('Não foi possível'), String(e?.message ?? e)); p.fim(null); }); }, 300);
  };
  const tela = (<>
    <ActionSheet title={t('Ler com a minha IA')} onClose={fechar} actions={aberta ? [
      ...(pedido.current?.ja ? [{ label: '📎 ' + t('Usar o arquivo já anexado ({nome})', { nome: pedido.current.ja.nome }), onPress: () => { const j = pedido.current?.ja; rodar(async () => (j ? { arq: j } : null)); } }] : []),
      { label: '🖼️ ' + t('Fotos'), onPress: () => rodar(prot => pegar('fotos', prot)) },
      ...(Platform.OS !== 'web' ? [{ label: '📷 ' + t('Tirar foto'), onPress: () => rodar(prot => pegar('camera', prot)) }] : []),
      { label: '📎 ' + t('Arquivos'), onPress: () => rodar(prot => pegar('arquivos', prot)) },
    ] : null} />
    {anexo.tela}
  </>);
  return { escolher, tela };
}
